// The waste area's ONE mutation-failure note. A WRITE's refusal carries three
// facts, in this order: the area's own sentence naming the VERB that failed,
// the server's own problem `title` when the refusal produced one, and the
// `request_id` — the same request-id line the §4 read card keeps
// (design-system §3.9; lib/request exists to carry it).
//
// HISTORY, because both halves of this file were incidents:
//
//   * Until the #85 audit (task #100) S2's two notes said only the sentence —
//     but the failure a supervisor most needs to quote to support is precisely
//     the mutation the server REFUSED, and a note without the id is a dead
//     end. The id is absent only when the failure never produced a server
//     answer (a network cut), and then only the sentence renders.
//   * Until task #105 S5 had a PRIVATE second variant that rendered one fixed
//     house string for all four lifecycle verbs — no server sentence, no id,
//     no verb. "The action was refused — the piece may have changed under you"
//     is a guess; `409 REMNANT_NOT_AVAILABLE`'s own title is the fact the
//     storekeeper acts on. The extraction exists so the two screens cannot
//     drift again: a third hand-rolled `.waste-error-note` in this area is the
//     defect this file is here to prevent.
//
// The server's title renders AS the server's — labelled and untranslated
// (problem titles are wire English, D-022 keeps the translatable machinery in
// `errors[].message_key`) — so a Russian screen shows a Russian sentence
// naming the verb and then the server's own words, never a paraphrase of them.
// It renders only when the SERVER actually said it: `RequestError.message`
// falls back to the hook's English label when the body was not a problem, and
// printing that fallback would dress a network cut up as a server sentence.

import { useTranslation } from "react-i18next";
import { RequestError, requestIdOf } from "../../../lib/request";

export function MutationFailureNote({
  text,
  error,
  "data-testid": testId,
}: {
  text: string;
  error: unknown;
  "data-testid"?: string;
}) {
  const { t } = useTranslation();
  const requestId = requestIdOf(error);
  const serverTitle = error instanceof RequestError ? error.problem?.title : undefined;
  return (
    <p className="waste-error-note" role="alert" data-testid={testId}>
      {text}
      {serverTitle !== undefined && serverTitle.length > 0 ? (
        <> {t("waste.common.serverSaid", { title: serverTitle })}</>
      ) : null}
      {requestId !== undefined ? (
        <>
          {" "}
          {t("waste.common.requestId")}: <code>{requestId}</code>
        </>
      ) : null}
    </p>
  );
}
